import { Component, forwardRef, ChangeDetectorRef, Input, Output } from '@angular/core';
import { PlatformApiService, PlanSearchCriteria, ResultFilter } from '../../services/platform-api.service';
import { SessionService } from 'src/app/services/session.service';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { Account, Plan, AccountType, InstanceStatusType, RoleType} from '../../models/AccountModel';
import { WizardScreenComponent } from 'src/app/shared/wizard.component';
import { ProfileConstants } from '../../shared/profile-constants';
import { NistAssetValues } from 'src/app/services/assessment.service';
import { Assessment } from 'src/app/models/AssessmentModel';
import { InfiniteScrollOption } from 'src/app/shared/infinite-scroll-select.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationComponent } from 'src/app/shared/confirmation.component';
import * as moment from 'moment';

@Component({
  selector: 'cw-ci-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  providers: [{provide: WizardScreenComponent, useExisting: forwardRef(() => AccountSettingsComponent)}]
})
export class AccountSettingsComponent extends WizardScreenComponent {
  @Input()
  account: Account;
  @Input()
  parentAccount: Account;
  RoleType = RoleType;

  private _useCompanyDropdown: boolean;
  @Input()
  get useCompanyDropdown(): boolean {
    return this._useCompanyDropdown;
  }
  set useCompanyDropdown(val: boolean) {
    this._useCompanyDropdown = val;
    if(val) {
      this.form.controls.accountNameText.setValidators(null);
      this.form.controls.accountNameText.updateValueAndValidity();
    } else {
      this.form.controls.accountNameText.setValidators([Validators.required]);
      this.form.controls.accountNameText.updateValueAndValidity();
    }
  }
  availablePlans: Plan[];

  planAssessments: Assessment[] = [];
  PROFILE_CONSTANTS = ProfileConstants;
  csAccountDtls: any;
  iscsSave: any;
  csPartnerConfirmation = false;
  isCSEnabled= false;
  isNewAccountInTrial=true;

  constructor(public sessionService: SessionService, private _apiService: PlatformApiService, private _cd: ChangeDetectorRef, public dialog: MatDialog) {
    super();
    this.form = new FormGroup ({
      accountNameText: new FormControl('', [Validators.required]),
      accountNameDropdown: new FormControl('', []),
      automateClientDropdown: new FormControl('', []),
      centralServicePartnerId: new FormControl({value: '', disabled: true}, []),
      instanceId: new FormControl({value: '', disabled: true}, []),
      plan: new FormControl('', [Validators.required]),
      assessment: new FormControl(''),
      Industry: new FormControl('', [Validators.required]),
      Employees: new FormControl('', [Validators.required]),
      Location: new FormControl('', [Validators.required]),
      instanceStatus: new FormControl('', []),
      paymentStatus: new FormControl('', []),
      trialPeriodEndDate: new FormControl('', []),
      billingStartDate: new FormControl({value: '', disabled: true})
      });

    this.form.controls.plan.valueChanges.subscribe(value => {
      console.log('plan changed value:', value);

      if (this.availablePlans && this.account.id == undefined) {
        const planObj = this.availablePlans.find(el => el.id == value);
        if (planObj) {
          this.isNewAccountInTrial = true;
          if (this.account.parentId == undefined) {
            if (this.account.id) {
              this.isNewAccountInTrial = this.account.isInTrial();
            } else if (planObj.trialPeriodDays == undefined || planObj.trialPeriodDays == 0) {
              this.isNewAccountInTrial = false;
            }
          } else {
            this.isNewAccountInTrial = this.account.isInTrial();
          }
          if (planObj.getFeature('ALLOWED_ASSESSMENT_ID', this.isNewAccountInTrial)) {
            this._apiService.getAssessments(new ResultFilter(undefined, undefined, undefined,undefined, {planId: value, isInTrial: this.isNewAccountInTrial, excludeTypeList: ['CERTIFICATION']}))
              .subscribe(assessmentTemplates => {
                this.planAssessments = assessmentTemplates.results;
                if (assessmentTemplates.count == 1) {
                  this.form.controls.assessment.setValue(new InfiniteScrollOption(assessmentTemplates.results[0].name, assessmentTemplates.results[0].id, false, true, true));
                }
                this.form.controls.assessment.setValidators([Validators.required]);
                this.form.controls.assessment.updateValueAndValidity();
            });
          } else {
            this.planAssessments.length = 0;
            this.form.controls.assessment.setValidators([]);
            this.form.controls.assessment.updateValueAndValidity();
          }
        }
      }
    });
  }

  onLoad() {
    // If the account settings component is the first item displayed when the wizard opens, it will
    // throw an ExpressionChangedAfterItHasBeenCheckedError due to manipulating the form controls. This
    // is because in the initial load scenario, change detection has completed for the cycle before angular
    // gets to this method. We detach, make our changes, reattach, and trigger detection manually.
    this._cd.detach();
    this.showSpinner();

    if(!this.sessionService.hasRole(RoleType.ADMIN) && this.sessionService.isAccountType('SYSTEM')) {
      this.form.disable();
    }

    if (this.account.type != 'SYSTEM' && (!this.sessionService.isAccountType('SYSTEM') && this.account.id != undefined)) {
      this.form.controls.plan.disable();
    }

    if (this.account.id != undefined) {
      this.form.controls.assessment.clearValidators();
    }

    this._apiService.getPlans(new PlanSearchCriteria(true, undefined, this.account.parentId)).subscribe(plans => {
      this.availablePlans = plans.results;

      if (this.availablePlans.length == 1) {
        this.form.controls.plan.setValue(this.availablePlans[0].id);
      }
    });

    this.form.reset();

    if(this.account.id > 0 && this.account.parentId){
      this.form.controls.accountNameDropdown.setValue(new InfiniteScrollOption(this.account.name, this.account.partner ? this.account.partner.companyRecId : null, false, false, true));
      if (this.account.automateIntegration && this.account.automateIntegration.clientId) {
        this.form.controls.automateClientDropdown.setValue(new InfiniteScrollOption(this.account.automateIntegration.clientName, this.account.automateIntegration.clientId, false, false, true));
      }
    }

    if (this.account.automateIntegration) {

    }

    this.form.controls.accountNameText.setValue(this.account.name);

    if (this.account.centralServicePartnerId) {
      this.form.controls.centralServicePartnerId.setValue(new InfiniteScrollOption(this.account.centralServicePartnerId, this.account.centralServicePartnerId, false, false, true));
    }

    if(this.account.instanceId) {
      this.form.controls.instanceId.setValue(new InfiniteScrollOption(this.account.instanceId, this.account.instanceId, false, false, true));
    }

    if (this.account && this.account.planSubscription) {
      this.form.controls.plan.setValue(this.account.planSubscription.planId);
    }

    if (this.account.type == AccountType.SYSTEM) {
      this.form.controls.Industry.setValidators(null);
      this.form.controls.Employees.setValidators(null);
      this.form.controls.Location.setValidators(null);
      this.form.controls.Industry.updateValueAndValidity();
      this.form.controls.Employees.updateValueAndValidity();
      this.form.controls.Location.updateValueAndValidity();
      this.hideSpinner()
      this._cd.reattach();
      this._cd.detectChanges();
      super.onLoad();
    } else if (this.account.id != undefined) {

      this.getCSdata();

      if (this.account.planSubscription) {
        this.form.controls.plan.setValue(this.account.planSubscription.planId);
        this.form.controls.billingStartDate.setValue(this.account.planSubscription.billingStartDate);
      }

      this._apiService.getAccountProfileData(this.account.id).subscribe(data => {
        this.form.controls.Industry.setValue(data.industry);
        this.form.controls.Employees.setValue(data.employees);
        this.form.controls.Location.setValue(data.location);

        this.hideSpinner()
        this._cd.reattach();
        this._cd.detectChanges();
        super.onLoad();
      });
    } else {
      this.hideSpinner()
      this._cd.reattach();
      this._cd.detectChanges();
      super.onLoad();
    }
  }

  getCSdata(){
    const filter = new ResultFilter();
    if (this.account.centralServicePartnerId && this.account.instanceId){
      filter.searchData = { partner: this.account.centralServicePartnerId, instance: this.account.instanceId };
      this._apiService.getProductInstanceDtls(filter).subscribe(
        resp => {
          this.csAccountDtls = resp
          let dbTrialDate;
          let isEndDateValid;
          const csTrialDate =  this.csAccountDtls.trialInfo.end ? moment(this.csAccountDtls.trialInfo.end*1000).format('YYYY-MM-DD') : null;
          if(this.account.instanceStatus != this.csAccountDtls.instanceStatus.identifier){
            if (!(this.csAccountDtls.instanceStatus.identifier.toLowerCase() in InstanceStatusType)){
              this.form.controls.instanceStatus.setValue(null);
            } else {
              this.form.controls.instanceStatus.setValue(this.csAccountDtls.instanceStatus.identifier);
            }
          } else {
            this.form.controls.instanceStatus.setValue(this.account.instanceStatus);
          }

        if (this.account.planSubscription) {
          if(this.account.planSubscription.paymentStatus != this.csAccountDtls.paymentStatus.identifier){
            this.form.controls.paymentStatus.setValue(this.csAccountDtls.paymentStatus.identifier);
          } else {
            this.form.controls.paymentStatus.setValue(this.account.planSubscription.paymentStatus);
          }
          dbTrialDate = moment.utc(this.account.planSubscription.trialPeriodEndDate).format('YYYY-MM-DD');
          if (csTrialDate != null){
            isEndDateValid = moment(dbTrialDate).isSame(csTrialDate)
          } else {
            isEndDateValid = (csTrialDate == null && this.account.planSubscription.trialPeriodEndDate == null) ? true : false;
          }
          if(isEndDateValid){
            this.form.controls.trialPeriodEndDate.setValue(dbTrialDate);
          } else {
            this.form.controls.trialPeriodEndDate.setValue(csTrialDate);
          }
        }
        if(this.account.instanceStatus != this.csAccountDtls.instanceStatus.identifier || this.account.planSubscription.paymentStatus != this.csAccountDtls.paymentStatus.identifier || !isEndDateValid){
          if (!(this.csAccountDtls.instanceStatus.identifier.toLowerCase() in InstanceStatusType)){
            this.account.instanceStatus = null;
          } else {
            this.account.instanceStatus = this.csAccountDtls.instanceStatus.identifier;
          }
          this.account.planSubscription.paymentStatus = this.csAccountDtls.paymentStatus.identifier;
          const todayDate = csTrialDate ? new Date(Date.parse(csTrialDate)) : null;
          this.account.planSubscription.trialPeriodEndDate = todayDate;
          const alertMsg = 'Account payment status, instance status and/or trial date were different than those in the Central Services database and have been updated.'
          this.csDataAlert(alertMsg);
          this.iscsSave = true;
          this.onSave();
        }
        this.iscsSave = false;
        }
      );
    } else {
      this.form.controls.paymentStatus.setValue(this.account.planSubscription.paymentStatus);
      this.form.controls.instanceStatus.setValue(this.account.instanceStatus);
      if (this.account.planSubscription) {
        this.form.controls.trialPeriodEndDate.setValue(this.account.planSubscription.trialPeriodEndDate);
      }
    }
  }

  csDataAlert(alertMsg){
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      panelClass: 'flush-dialog',
      data: {
        promptMessage: [alertMsg],
        height: 'auto',
        showClose: false
      }
    });
  }

  onSave() {
    if (this.form.valid) {
      if (this.isCSEnabled){
        const oldPId = this.account.centralServicePartnerId ? this.account.centralServicePartnerId: null;
        const oldInstId = this.account.instanceId? this.account.instanceId: null;
        const newPId = this.form.controls.centralServicePartnerId? this.form.controls.centralServicePartnerId.value : undefined;
        const newInstId = this.form.controls.instanceId? this.form.controls.instanceId.value : undefined;
        if (newPId && newInstId) {
          if (oldPId != newPId || oldInstId != newInstId) {
            this.validateCSPartnerInstance(oldPId, oldInstId , newPId, newInstId )
          }
        } else {
          const alertmessage = `Partner ID, Instance ID cannot be null.`
          this.displaydialog(alertmessage, false);
        }
      } else {
        this.saveAccountDetails();
      }
    }
  }

  saveAccountDetails() {
    if(this.iscsSave == undefined || !this.iscsSave){
      this.showSpinner('Saving...');
    }
    const accountJson = {
      type: this.account.type,
      planId: this.form.value.plan,
      parentId: this.account.parentId,
      trialPeriodEndDate: this.form.value.trialPeriodEndDate,
      paymentStatus: this.form.value.paymentStatus,
      instanceStatus: this.form.value.instanceStatus,
      assessmentTemplateId: this.form.value.assessment?.value,
      referralCode: this.account.referralCode,
      name: undefined,
      partner: undefined,
      automateIntegration: undefined,
      centralServicePartnerId: undefined,
      instanceId: undefined
    }

    if(this.useCompanyDropdown) {
      accountJson.name = this.form.value.accountNameDropdown.description;
      accountJson.partner = {
        key: 'connectwise',
        companyRecId: this.form.value.accountNameDropdown.value,
      };
    } else {
      accountJson.name = this.form.value.accountNameText;
    }

    if (this.form.value.automateClientDropdown && this.form.value.automateClientDropdown.value) {
      accountJson.automateIntegration = {
        clientId: this.form.value.automateClientDropdown.value,
        clientName: this.form.value.automateClientDropdown.description
      }
    }

    if(this.csPartnerConfirmation) {
      if (this.form.controls.centralServicePartnerId && this.form.controls.centralServicePartnerId.value) {
        accountJson.centralServicePartnerId =  this.form.controls.centralServicePartnerId.value.value
      }

      if (this.form.controls.instanceId && this.form.controls.instanceId.value) {
        accountJson.instanceId =  this.form.controls.instanceId.value.value
      }
    } else {
      accountJson.centralServicePartnerId = this.account.centralServicePartnerId;
      accountJson.instanceId = this.account.instanceId;
    }

    if (this.account.id) {
      this.updateExistingAccount(accountJson);
    } else {
      this.saveNewAccount(accountJson);
    }
  }

  saveNewAccount(accountJson) {

    accountJson.profile = {
      industry: this.form.value.Industry,
      employees: this.form.value.Employees,
      location: this.form.value.Location
    };

    this._apiService.saveAccount(undefined, { account: accountJson }, this.account.parentId).subscribe(
      resp => {
        this.account = new Account(resp);
        this.form.reset();
        super.onSave();
        this.notifyWizardComponent('SAVE_ACCOUNT', this.account);
      }, err => {
        this.hideSpinner();
        switch (err.error.code) {
          default:
            this.form.setErrors({saveFailed: true});
        }
      }
    );
  }

  updateExistingAccount(accountJson) {
    const csSave = this.iscsSave == undefined || !this.iscsSave ? false : true;
    this._apiService.saveAccount(this.account.id, { account: accountJson }, this.account.parentId).subscribe(
      resp => {
        this.account = new Account(resp);
        if (this.account.type != AccountType.SYSTEM) {
          this.saveProfile().subscribe(() => {
            this.saveAssets().subscribe(() => {
              this.hideSpinner()
              super.onSave();
              if(!csSave) {
                this.form.reset();
                this.notifyWizardComponent('SAVE_ACCOUNT', this.account);
              } else {
                this.notifyWizardComponent('SAVE_CS_DETAILS', this.account);
              }
            }, (err) => {
              this.hideSpinner();
              switch (err.error.code) {
                default:
                  this.form.setErrors({saveFailed: true});
              }
            });
          }, err => {
            this.hideSpinner();
            switch (err.error.code) {
              default:
                this.form.setErrors({saveFailed: true});
            }
          });
        } else {
          this.hideSpinner()
          super.onSave();
          if(!csSave) {
            this.notifyWizardComponent('SAVE_ACCOUNT', this.account);
          } else {
            this.notifyWizardComponent('SAVE_CS_DETAILS', this.account);
          }
        }
      }, err => {
        this.hideSpinner();
        switch (err.error.code) {
          default:
            this.form.setErrors({saveFailed: true});
        }
      }
    );
  }

  saveProfile() {
    return this._apiService.saveAccountProfileData(this.account.id, {
      industry: this.form.value.Industry,
      employees: this.form.value.Employees,
      location: this.form.value.Location
    }, this.account.parentId);
  }

  saveAssets() {
    let mult = 1;
    switch (this.form.value.Employees) {
      case '501 - 1000': mult = 5; break;
      case '1001 +': mult = 20; break;
      default: mult = 1; break;
    }
    const assets = new NistAssetValues(mult);
    return this._apiService.saveAccountAssetData(this.account.id, {
      equipment: assets.equipment,
      software: assets.software,
      people: assets.people,
      environment: assets.environment,
      data: assets.data,
      information_systems: assets.informationSystems,
      organization: assets.organization,
      third_parties: assets.thirdParties,
      all: assets.all
    }, this.account.parentId);
  }

  forbiddenNames(control: AbstractControl) {
    const selection: any = control.value;
    if (typeof selection === 'string') {
      return  this.form.controls.accountNameDropdown.setErrors({incorrect: true});
    }
    return null
  }

  forbiddenFreeForm(control: AbstractControl) {
    const selection: any = control.value;
    if (typeof selection === 'string') {
      if (this.form.controls.instanceId.touched) {
        return  this.form.controls.instanceId.setErrors({incorrect: true});
      } else if(this.form.controls.centralServicePartnerId.touched) {
        return  this.form.controls.centralServicePartnerId.setErrors({incorrect: true});
      }
    }
    return null
  }

  enablePartnerDropdown() {
    this.isCSEnabled = true;
    this.form.controls.centralServicePartnerId.enable();
  }

  handleEvent(event) {
    if(event.state == 'ONSELECT') {
      if(event.selection.option.value) {
        const filter = new ResultFilter();
        const values = event.selection.option.value;
        if (values.data.key.toLowerCase() == 'centralserviceinstanceid') {
          this.showSpinner('Loading...');
          const userDetails = localStorage.getItem('workingCredential')
          var obj = JSON.parse(userDetails);
          filter.searchData.searchField= 'centralServicePartnerId';
          filter.searchData.name= values.data.partnerId;
          filter.searchData.userProfile = obj.user.id;
          this._apiService.getCSAllTenants(filter).subscribe( resp => {
            const result = resp.results;
            this.hideSpinner();
            if(result.length == 1) {
              this.form.controls.centralServicePartnerId.setValue(new InfiniteScrollOption(result[0].id+ '('+result[0].companyName+')', result[0].id, false, false, true));
              this.form.controls.centralServicePartnerId.updateValueAndValidity();
            }
          }, err => {
            this.hideSpinner();
            switch (err.error.code) {
              default:
                this.form.controls.centralServicePartnerId.setValue(new InfiniteScrollOption('No results found.',null, true, false, true));
                this.form.controls.centralServicePartnerId.updateValueAndValidity();
            }
          });
        }
      }
    }
  }

  enableInstanceDropdown() {
    this.isCSEnabled = true;
    this.form.controls.instanceId.enable();
  }

  validateCSPartnerInstance(oldPId, oldInstId, newPId, newInstId) {
    this.showSpinner('Loading...');
    const filter = new ResultFilter();

    filter.searchData = { partner: newPId.value, instance: newInstId.value };
    this._apiService.getProductInstanceDtls(filter).subscribe(
      resp => {
        let alertmessage;
        if(resp) {
          if(oldPId && oldInstId) {
            alertmessage = `Partner ID: ${oldPId} <br> Instance ID: ${oldInstId} <br><br>`;
          } else {
            alertmessage = `Partner ID, Instance ID`;
          }
          alertmessage = `${alertmessage} has been updated and the new account will be <br><br>Partner: ${newPId.description} <br> Instance: ${newInstId.description}. <br><br> Are you sure?`
          this.displaydialog(alertmessage, true);
        } else {
          alertmessage = `Partner ID: ${newPId.value} <br> Instance ID: ${newInstId.value} <br> <br>product instance not found in central services database and hence cannot be updated.`;
          this.displaydialog(alertmessage, false);
        }
      }
    )
  }

  displaydialog(message, showLabel) {
    const labels=  (showLabel == true)? [{label:'Yes', value:'YES', color:'primary'}]: [{label:'OK', value:'OK', color:'primary'}];
    this.hideSpinner();
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      panelClass: 'flush-dialog',
      data: {
        promptMessage: [message],
        closeLabel: (showLabel == true)? 'No': '',
        buttons: labels,
        height: 'auto',
        showClose: (showLabel == true)? true : false
      }
    });
    dialogRef.afterClosed().subscribe(confirm => {
      this.form.controls.centralServicePartnerId.disable();
      this.form.controls.instanceId.disable();
      this.isCSEnabled = false;
      if(confirm != 'YES') {
        this.onLoad();
        return ''
      } else {
        this.csPartnerConfirmation = true;
      }
      this.saveAccountDetails();
    });
  }
}
